import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/Home.css";

const Home = () => {
  const [earthquakes, setEarthquakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [noticias, setNoticias] = useState([]);
  const [reportesEspeciales, setReportesEspeciales] = useState([]);

  useEffect(() => {
    fetchEarthquakeData();
    fetchNoticias();
    fetchReportesEspeciales();
    const interval = setInterval(fetchEarthquakeData, 60000);

    return () => clearInterval(interval);
  }, []);

  const fetchNoticias = async () => {
    try {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/data/noticias.json`,
      );
      if (response.ok) {
        const data = await response.json();
        // Get only the 5 most recent news items
        setNoticias(data.slice(0, 5));
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching noticias:", error);
      }
      // Keep empty array if fetch fails
    }
  };

  const fetchReportesEspeciales = async () => {
    try {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/data/reportes-especiales.json`,
      );
      if (response.ok) {
        const data = await response.json();
        // Sort by fecha descendente (more recent first) and get the 2 most recent
        const sorted = [...data].sort((a, b) => {
          const dateA = new Date(
            a.fecha.replace(" de ", "-").replace("de ", ""),
          );
          const dateB = new Date(
            b.fecha.replace(" de ", "-").replace("de ", ""),
          );
          return dateB - dateA;
        });
        setReportesEspeciales(sorted.slice(0, 2));
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching reportes especiales:", error);
      }
      // Keep empty array if fetch fails
    }
  };

  const fetchEarthquakeData = async () => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("Fetching earthquake data...");
      }
      const response = await fetch(
        "https://resnom.cicese.mx/earthquakes/feed/v1.0/summary/2.5_week.geojson",
      );
      if (process.env.NODE_ENV === "development") {
        console.log("Response status:", response.status);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (process.env.NODE_ENV === "development") {
        console.log("Data received:", data);
      }

      if (data.features && data.features.length > 0) {
        const recentEarthquakes = data.features.slice(0, 5).map((feature) => ({
          id: feature.properties.ids,
          magnitude: feature.properties.mag,
          place: feature.properties.place,
          lat: feature.geometry?.coordinates?.[1],
          lng: feature.geometry?.coordinates?.[0],
          time: feature.properties.time,
          timeUTC: new Date(feature.properties.time).toLocaleString(undefined, {
            timeZone: "UTC",
          }),
          timeLocal: new Date(feature.properties.time).toLocaleString(
            undefined,
            {
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          ),
        }));

        if (process.env.NODE_ENV === "development") {
          console.log("Processed earthquakes:", recentEarthquakes);
        }
        setEarthquakes(recentEarthquakes);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.log("No features found in data");
        }
        setEarthquakes([]);
      }

      setLoading(false);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching earthquake data:", error);
      }
      setLoading(false);
      setEarthquakes([
        {
          magnitude: "4.2",
          place: "Baja California, México",
          timeUTC: "2024-01-15 10:30:00",
          timeLocal: "2024-01-15 02:30:00",
        },
        {
          magnitude: "3.8",
          place: "Sonora, México",
          timeUTC: "2024-01-15 09:15:00",
          timeLocal: "2024-01-15 01:15:00",
        },
      ]);
    }
  };

  const getAgeColor = (time) => {
    const age = Date.now() - time;
    if (age <= 3600000) return "#ff0000"; // Última hora  — rojo
    if (age <= 86400000) return "#ff7800"; // Último día   — naranja
    if (age <= 604800000) return "#ffff00"; // Última semana — amarillo
    if (age <= 2592000000) return "#d9d9d9"; // Último mes  — gris claro
    return "#9e9e9e"; // Más antiguo  — gris
  };

  const getMagnitudeRadius = (mag) => {
    const m = parseFloat(mag) || 0;
    return Math.max(5, m * 3);
  };

  return (
    <div className="presentation-page pt-16">
      {/* Hero Section */}
      <header className="hero-section">
        <div className="hero-background">
          <img
            src={`${process.env.PUBLIC_URL}/assets/img/resnom-oficina.png`}
            alt="Campus CICESE"
          />
        </div>
        <div className="hero-content">
          <div className="hero-container">
            {/* Hero Card */}
            <div className="hero-card">
              <h1 className="hero-title">
                Red Sísmica del
                <span className="hero-title-gradient"> Noroeste de México</span>
              </h1>
              <h2 className="hero-subtitle">RESNOM</h2>
              <p className="hero-description">
                Monitoreo de la actividad sísmica en el noroeste de México.
              </p>
              <div className="hero-buttons">
                <a
                  href="https://resnom.cicese.mx/earthquakes/map/"
                  className="hero-button-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Actividad Reciente
                </a>
                <a
                  href="https://resnom.cicese.mx/earthquakes/search/"
                  className="hero-button-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Catálogo
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-globe"></i>
              </div>
              <h3 className="feature-title">Conoce la RESNOM</h3>
              <p className="feature-description">
                Información acerca de la Red, como está conformada y datos sobre
                su instrumentación.
              </p>
              <Link to="/acerca" className="feature-link">
                Leer Más
              </Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-book"></i>
              </div>
              <h3 className="feature-title">Catálogos</h3>
              <p className="feature-description">
                Consulta la sismicidad histórica y descarga los catálogos de la
                actividad de la región.
              </p>
              <Link to="/boletines" className="feature-link">
                Ver Catálogos
              </Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-bullhorn"></i>
              </div>
              <h3 className="feature-title">Divulgación</h3>
              <p className="feature-description">
                RESNOM responde las preguntas más frecuentes del público y
                ofrece material didáctico.
              </p>
              <Link to="/divulgacion" className="feature-link">
                Leer Más
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="earthquake-section" id="ulitmos-sismos">
        <div className="wave-top">
          <svg
            width="100%"
            viewBox="0 -2 1920 157"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <title>wave-down</title>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g fill="#FFFFFF" fillRule="nonzero">
                <g>
                  <path
                    d="M0,60.8320331 C299.333333,115.127115 618.333333,111.165365 959,47.8320321 C1299.66667,-15.5013009 1620.66667,-15.2062179 1920,47.8320331 L1920,156.389409 L0,156.389409 L0,60.8320331 Z"
                    transform="translate(960.000000, 78.416017) rotate(180.000000) translate(-960.000000, -78.416017) "
                  ></path>
                </g>
              </g>
            </g>
          </svg>
        </div>

        <div className="earthquake-container">
          <h2 className="earthquake-title">
            Últimos{" "}
            <span className="special-reports-title-gradient">Sismos</span>
          </h2>

          <div className="earthquake-content-grid">
            {/* Tabla de sismos */}
            <div className="earthquake-card">
              <table className="earthquake-table">
                <thead>
                  <tr>
                    <th>Magnitud</th>
                    <th>Lugar</th>
                    <th>Fecha (UTC)</th>
                    <th>Fecha (Local)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        Cargando datos...
                      </td>
                    </tr>
                  ) : earthquakes.length > 0 ? (
                    earthquakes.map((earthquake, index) => (
                      <tr
                        key={index}
                        className="earthquake-row"
                        onClick={() => {
                          if (earthquake.id) {
                            const cleanId = earthquake.id
                              .replace(/[,]/g, "")
                              .trim();
                            const url = `https://resnom.cicese.mx/earthquakes/eventpage/${cleanId}/executive`;
                            window.open(url, "_blank");
                          }
                        }}
                        style={{
                          cursor: earthquake.id ? "pointer" : "default",
                        }}
                      >
                        <td className="earthquake-magnitude">
                          {earthquake.magnitude}
                        </td>
                        <td className="earthquake-place">{earthquake.place}</td>
                        <td className="earthquake-time">
                          {earthquake.timeUTC}
                        </td>
                        <td className="earthquake-time">
                          {earthquake.timeLocal}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        No hay datos disponibles
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mapa de sismicidad reciente */}
            <div className="earthquake-map-wrapper">
              <MapContainer
                center={[29.5, -114.5]}
                zoom={5}
                scrollWheelZoom={false}
                zoomControl={true}
                className="earthquake-map-container"
              >
                {/* Basemap Esri Light Gray Canvas — mismo que la app de monitoreo */}
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                  attribution="Esri, HERE, Garmin, &copy; OpenStreetMap contributors, and the GIS User Community"
                />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                  attribution=""
                />
                {[...earthquakes]
                  .sort((a, b) => {
                    const priority = (time) => {
                      const age = Date.now() - time;
                      if (age <= 3600000) return 3; // rojo — encima de todo
                      if (age <= 86400000) return 2; // naranja
                      if (age <= 604800000) return 1; // amarillo
                      return 0; // blanco/gris — abajo
                    };
                    return priority(a.time) - priority(b.time);
                  })
                  .map((earthquake, index) =>
                    earthquake.lat && earthquake.lng ? (
                      <CircleMarker
                        key={index}
                        center={[earthquake.lat, earthquake.lng]}
                        radius={getMagnitudeRadius(earthquake.magnitude)}
                        pathOptions={{
                          color: "#555",
                          weight: 0.8,
                          fillColor: getAgeColor(earthquake.time),
                          fillOpacity: 0.85,
                        }}
                        eventHandlers={{
                          click: () => {
                            if (earthquake.id) {
                              const cleanId = earthquake.id
                                .replace(/[,]/g, "")
                                .trim();
                              window.open(
                                `https://resnom.cicese.mx/earthquakes/eventpage/${cleanId}/executive`,
                                "_blank",
                              );
                            }
                          },
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -4]}>
                          <span>
                            <strong>M {earthquake.magnitude}</strong>
                            <br />
                            {earthquake.place}
                          </span>
                        </Tooltip>
                      </CircleMarker>
                    ) : null,
                  )}
              </MapContainer>
              <div className="earthquake-map-legend">
                <span className="legend-label">Edad:</span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: "#ff0000" }}
                  ></span>
                  Hora
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: "#ff7800" }}
                  ></span>
                  Día
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: "#ffff00", border: "1px solid #ccc" }}
                  ></span>
                  Semana
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ background: "#d9d9d9", border: "1px solid #ccc" }}
                  ></span>
                  Mes
                </span>
              </div>
            </div>
          </div>

          <div className="earthquake-cta">
            <a
              href="https://resnom.cicese.mx/earthquakes/map/"
              target="_blank"
              rel="noopener noreferrer"
              className="earthquake-cta-link"
            >
              Ver mapa completo con toda la actividad &rarr;
            </a>
          </div>
        </div>

        <div className="wave-bottom">
          <svg
            width="100%"
            viewBox="0 -1 1920 166"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <title>wave-up</title>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g
                transform="translate(0.000000, 5.000000)"
                fill="#FFFFFF"
                fillRule="nonzero"
              >
                <g transform="translate(0.000000, -5.000000)">
                  <path d="M0,70 C298.666667,105.333333 618.666667,95 960,39 C1301.33333,-17 1621.33333,-11.3333333 1920,56 L1920,165 L0,165 L0,70 Z"></path>
                </g>
              </g>
            </g>
          </svg>
        </div>
      </section>
      <section className="special-reports-section">
        <div className="special-reports-container">
          <div className="special-reports-grid">
            <div className="special-reports-content">
              <h2 className="special-reports-title">
                Reportes{" "}
                <span className="special-reports-title-gradient">
                  Especiales
                </span>
              </h2>
              <p className="special-reports-description">
                En esta sección encontrarás reportes sobre sismos que han
                llamado la atención por su ubicación o intensidad. Están
                preparados por el equipo de la Red Sísmica del noroeste de
                México y presentan, de forma sencilla, datos como el lugar del
                epicentro, magnitud y mapas que ayudan a entender mejor el
                evento.
              </p>
            </div>

            <div className="special-reports-list">
              {reportesEspeciales.map((reporte) => (
                <div key={reporte.id} className="special-report-item">
                  <a
                    href={reporte.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="special-report-link"
                  >
                    <div className="special-report-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                          fill="currentColor"
                        />
                        <path d="M14 2V8H20" fill="currentColor" />
                        <path d="M16 13H8V11H16V13Z" fill="currentColor" />
                        <path d="M16 17H8V15H16V17Z" fill="currentColor" />
                        <path d="M10 9H9V7H10V9Z" fill="currentColor" />
                      </svg>
                    </div>
                    <div className="special-report-content">
                      <div className="special-report-title">
                        {reporte.titulo}
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="news-section">
        <div className="news-container">
          <div className="news-header">
            <h2 className="news-title">
              Noticias <span className="news-title-gradient">Recientes</span>
            </h2>
            <p className="news-description">
              Mantente al día con las actividades, proyectos y novedades de la
              Red. Aquí compartimos noticias relevantes sobre la investigación
              sísmica, actualizaciones tecnológicas, eventos de divulgación y
              otros temas de interés para la comunidad.
            </p>
          </div>

          <div className="news-carousel">
            {noticias.length > 0 ? (
              <>
                <div
                  className="news-carousel-container"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {noticias.map((noticia, index) => {
                    // Si imagenes está vacío o solo tiene 'default.webp', usar panoramic
                    // Si imagenes tiene otra imagen, usar esa
                    const hasRealImages =
                      noticia.imagenes &&
                      noticia.imagenes.length > 0 &&
                      noticia.imagenes[0] !== "default.webp";
                    const imagePath = hasRealImages
                      ? `${process.env.PUBLIC_URL}/assets/img/noticias/${noticia.imagenes[0]}`
                      : `${process.env.PUBLIC_URL}/assets/img/${index % 2 === 0 ? "panoramic.webp" : "panoramic2.webp"}`;
                    return (
                      <div key={noticia.id} className="news-slide">
                        <div
                          className="news-slide-image"
                          style={{
                            backgroundImage: `url('${imagePath}')`,
                          }}
                        ></div>
                        <div className="news-slide-overlay">
                          <h3 className="news-slide-title">{noticia.titulo}</h3>
                          <p className="news-slide-text">{noticia.resumen}</p>
                          <Link
                            to={`/noticias?id=${noticia.id}`}
                            className="news-slide-link"
                          >
                            Leer más →
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {noticias.length > 1 && (
                  <>
                    <div className="news-carousel-controls">
                      <button
                        className="news-carousel-button"
                        onClick={() =>
                          setCurrentSlide((prev) =>
                            prev === 0 ? noticias.length - 1 : prev - 1,
                          )
                        }
                        aria-label="Anterior"
                      >
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <button
                        className="news-carousel-button"
                        onClick={() =>
                          setCurrentSlide((prev) =>
                            prev === noticias.length - 1 ? 0 : prev + 1,
                          )
                        }
                        aria-label="Siguiente"
                      >
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="news-carousel-indicators">
                      {noticias.map((_, index) => (
                        <button
                          key={index}
                          className={`news-carousel-indicator ${currentSlide === index ? "active" : ""}`}
                          onClick={() => setCurrentSlide(index)}
                          aria-label={`Ir a slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="news-carousel-container">
                <div className="news-slide">
                  <div
                    className="news-slide-image"
                    style={{
                      backgroundImage: `url('${process.env.PUBLIC_URL}/assets/img/noticias/placeholder-noticia.jpg')`,
                    }}
                  ></div>
                  <div className="news-slide-overlay">
                    <h3 className="news-slide-title">Cargando noticias...</h3>
                    <p className="news-slide-text">
                      Las noticias más recientes se cargarán en un momento.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="institutions-section">
        <div className="institutions-container">
          <div className="institutions-header">
            <h2 className="institutions-title">
              Red Sismológica
              <span className="institutions-title-gradient"> del CICESE</span>
            </h2>
            <p className="institutions-description">
              La Red Sismológica del CICESE está conformada por una
              infraestructura de estaciones sísmicas de banda ancha,
              acelerométricas y geodésicas, las cuales registran continuamente
              la actividad sísmica en la región noroeste de México. Además,
              RESNOM mantiene una colaboración activa con diversas instituciones
              nacionales e internacionales, lo que permite el intercambio de
              datos en tiempo real, la estandarización de metodologías de
              análisis y el desarrollo de proyectos conjuntos orientados a la
              mejora del monitoreo sísmico.
            </p>
          </div>

          <div className="institutions-grid">
            <div className="institution-item">
              <a
                href="https://raubc.cicese.mx/"
                target="_blank"
                rel="noopener noreferrer"
                className="institution-link"
              >
                <img
                  className="institution-image"
                  src={`${process.env.PUBLIC_URL}/assets/img/images-networks/raubc_2.jpg`}
                  alt="RAUBC - Red Acelerográfica de la Universidad de Baja California"
                />
              </a>
            </div>

            <div className="institution-item">
              <a
                href="https://ranom.cicese.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="institution-link"
              >
                <img
                  className="institution-image"
                  src={`${process.env.PUBLIC_URL}/assets/img/images-networks/resnom-mf_2.jpg`}
                  alt="RANOM - Red Acelerográfica del Noroeste de México"
                />
              </a>
            </div>

            <div className="institution-item">
              <a
                href="https://resban.cicese.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="institution-link"
              >
                <img
                  className="institution-image"
                  src={`${process.env.PUBLIC_URL}/assets/img/images-networks/resban_2.jpg`}
                  alt="RESBAN - Red Sísmica de Baja California"
                />
              </a>
            </div>

            <div className="institution-item">
              <a
                href="https://regnom.cicese.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="institution-link"
              >
                <img
                  className="institution-image"
                  src={`${process.env.PUBLIC_URL}/assets/img/images-networks/regnom_2.jpg`}
                  alt="REGNOM - Red Geodésica del Noroeste de México"
                />
              </a>
            </div>

            <div className="institution-item">
              <a
                href="https://raubc.cicese.mx/raamm/"
                target="_blank"
                rel="noopener noreferrer"
                className="institution-link"
              >
                <img
                  className="institution-image"
                  src={`${process.env.PUBLIC_URL}/assets/img/images-networks/RAAM.jpg`}
                  alt="Red Acelerográfica del Área Metropolitana de Monterrey"
                />
              </a>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
